// Carnet d'entretien des véhicules : accès aux données.
//
// Le véhicule porte son état du moment (kilométrage relevé, prochaines
// échéances) ; les interventions gardent ce qui lui est arrivé. Migration 20.

import type { InterventionVehicule, VehiculeSuivi } from '@imp/shared/vehicules';

import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

export const TYPES_VEHICULE: { value: string; label: string }[] = [
  { value: 'utilitaire', label: 'Utilitaire' },
  { value: 'camion', label: 'Camion' },
  { value: 'voiture', label: 'Voiture' },
  { value: 'remorque', label: 'Remorque' },
  { value: 'nacelle', label: 'Nacelle' },
  { value: 'autre', label: 'Autre' },
];

export const CARBURANTS: { value: string; label: string }[] = [
  { value: '', label: '—' },
  { value: 'gazole', label: 'Gazole' },
  { value: 'essence', label: 'Essence' },
  { value: 'electrique', label: 'Électrique' },
  { value: 'hybride', label: 'Hybride' },
  { value: 'gpl', label: 'GPL' },
];

export function libelleTypeVehicule(v: string): string {
  return TYPES_VEHICULE.find((t) => t.value === v)?.label ?? v;
}

interface VehiculeRow {
  id: string;
  immatriculation: string;
  type: string;
  marque: string | null;
  modele: string | null;
  annee: number | null;
  couleur_agenda: string | null;
  actif: boolean;
  kilometrage: number | null;
  kilometrage_releve_le: string | null;
  carburant: string | null;
  date_achat: string | null;
  prix_achat_ht: string | null;
  derniere_ct: string | null;
  prochaine_ct: string | null;
  assurance_compagnie: string | null;
  assurance_expire_le: string | null;
  prochaine_revision_le: string | null;
  prochaine_revision_km: number | null;
  notes: string | null;
}

export type Vehicule = VehiculeRow;

const CHAMPS =
  'id,immatriculation,type,marque,modele,annee,couleur_agenda,actif,kilometrage,kilometrage_releve_le,carburant,date_achat,prix_achat_ht,derniere_ct,prochaine_ct,assurance_compagnie,assurance_expire_le,prochaine_revision_le,prochaine_revision_km,notes';

/** Une date de la base peut arriver horodatée ('2026-03-12T00:00:00') : on ne garde que le jour. */
const jourSeul = (v: string | null): string | null => (v === null ? null : v.slice(0, 10));

/** Le véhicule tel que la logique partagée l'attend. */
export function versSuivi(v: Vehicule): VehiculeSuivi {
  return {
    id: v.id,
    immatriculation: v.immatriculation,
    type: v.type,
    marque: v.marque,
    modele: v.modele,
    actif: v.actif,
    kilometrage: v.kilometrage,
    kilometrageReleveLe: jourSeul(v.kilometrage_releve_le),
    prochaineCt: jourSeul(v.prochaine_ct),
    assuranceExpireLe: jourSeul(v.assurance_expire_le),
    assuranceCompagnie: v.assurance_compagnie,
    prochaineRevisionLe: jourSeul(v.prochaine_revision_le),
    prochaineRevisionKm: v.prochaine_revision_km,
  };
}

export async function listVehicules(): Promise<Vehicule[]> {
  const { data, error } = await supabase
    .from('vehicules')
    .select(CHAMPS)
    .is('deleted_at', null)
    .order('immatriculation')
    .overrideTypes<VehiculeRow[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

export async function getVehicule(id: string): Promise<Vehicule> {
  const { data, error } = await supabase
    .from('vehicules')
    .select(CHAMPS)
    .eq('id', id)
    .single()
    .overrideTypes<VehiculeRow, { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

export interface VehiculeInput {
  immatriculation: string;
  type: string;
  marque: string | null;
  modele: string | null;
  annee: number | null;
  actif: boolean;
  kilometrage: number | null;
  kilometrage_releve_le: string | null;
  carburant: string | null;
  date_achat: string | null;
  prix_achat_ht: string | null;
  prochaine_ct: string | null;
  assurance_compagnie: string | null;
  assurance_expire_le: string | null;
  prochaine_revision_le: string | null;
  prochaine_revision_km: number | null;
  notes: string | null;
}

export async function createVehicule(input: VehiculeInput): Promise<string> {
  const { data, error } = await supabase
    .from('vehicules')
    .insert({ ...input, organization_id: ORG_ID })
    .select('id')
    .single()
    .overrideTypes<{ id: string }, { merge: false }>();
  if (error) throw new Error(error.message);
  return data.id;
}

export async function updateVehicule(id: string, input: VehiculeInput): Promise<void> {
  const { error } = await supabase.from('vehicules').update(input).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Retire un véhicule du parc. Suppression douce : les interventions restent
 * rattachées, et l'historique des chantiers où il a servi ne bouge pas.
 */
export async function archiverVehicule(id: string): Promise<void> {
  const { error } = await supabase
    .from('vehicules')
    .update({ actif: false, deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

// ----- Interventions --------------------------------------------------------

interface InterventionRow {
  id: string;
  vehicule_id: string;
  type: string;
  date_intervention: string;
  kilometrage: number | null;
  libelle: string;
  description: string | null;
  fournisseur: string | null;
  cout_ht: string;
  cout_ttc: string;
  facture_ref: string | null;
  prochaine_le: string | null;
  prochain_km: number | null;
}

export type Intervention = InterventionRow;

const CHAMPS_INTERVENTION =
  'id,vehicule_id,type,date_intervention,kilometrage,libelle,description,fournisseur,cout_ht,cout_ttc,facture_ref,prochaine_le,prochain_km';

export function versInterventionSuivi(i: Intervention): InterventionVehicule {
  return {
    id: i.id,
    vehiculeId: i.vehicule_id,
    type: i.type,
    dateIntervention: i.date_intervention.slice(0, 10),
    kilometrage: i.kilometrage,
    libelle: i.libelle,
    coutHt: Number(i.cout_ht),
    coutTtc: Number(i.cout_ttc),
  };
}

/** Tout le carnet, tous véhicules confondus (la liste s'en sert pour les coûts). */
export async function listInterventions(): Promise<Intervention[]> {
  const { data, error } = await supabase
    .from('vehicule_interventions')
    .select(CHAMPS_INTERVENTION)
    .is('deleted_at', null)
    .order('date_intervention', { ascending: false })
    .overrideTypes<InterventionRow[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

export async function listInterventionsVehicule(vehiculeId: string): Promise<Intervention[]> {
  const { data, error } = await supabase
    .from('vehicule_interventions')
    .select(CHAMPS_INTERVENTION)
    .eq('vehicule_id', vehiculeId)
    .is('deleted_at', null)
    .order('date_intervention', { ascending: false })
    .overrideTypes<InterventionRow[], { merge: false }>();
  if (error) throw new Error(error.message);
  return data;
}

export interface InterventionInput {
  vehicule_id: string;
  type: string;
  date_intervention: string;
  kilometrage: number | null;
  libelle: string;
  description: string | null;
  fournisseur: string | null;
  cout_ht: string;
  cout_ttc: string;
  facture_ref: string | null;
  prochaine_le: string | null;
  prochain_km: number | null;
}

export async function createIntervention(input: InterventionInput): Promise<void> {
  const { error } = await supabase
    .from('vehicule_interventions')
    .insert({ ...input, organization_id: ORG_ID });
  if (error) throw new Error(error.message);
}

export async function updateIntervention(id: string, input: InterventionInput): Promise<void> {
  const { error } = await supabase.from('vehicule_interventions').update(input).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteIntervention(id: string): Promise<void> {
  const { error } = await supabase
    .from('vehicule_interventions')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Reporte sur le véhicule ce que l'intervention vient d'apprendre : le
 * kilométrage du jour, et l'échéance qu'elle pose. C'est le geste que
 * l'utilisateur oublierait sinon, chaque fois.
 */
export async function reporterSurVehicule(
  vehiculeId: string,
  v: { kilometrage: number | null; date: string; prochaine_le: string | null; prochain_km: number | null; type: string },
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (v.kilometrage !== null) {
    patch.kilometrage = v.kilometrage;
    patch.kilometrage_releve_le = v.date;
  }
  if (v.prochaine_le !== null) {
    // Un contrôle technique repousse le CT ; tout le reste repousse la révision.
    if (v.type === 'controle_technique') patch.prochaine_ct = v.prochaine_le;
    else if (v.type === 'assurance') patch.assurance_expire_le = v.prochaine_le;
    else patch.prochaine_revision_le = v.prochaine_le;
  }
  if (v.prochain_km !== null) patch.prochaine_revision_km = v.prochain_km;
  if (Object.keys(patch).length === 0) return;
  const { error } = await supabase.from('vehicules').update(patch).eq('id', vehiculeId);
  if (error) throw new Error(error.message);
}
